/* EchoSense CRM — referrer "My investors" render config (STARSEED-CRM-011).
   ════════════════════════════════════════════════════════════════════════
   Plain data + pure helpers (the navConfig idiom) so the surface's field
   set is unit-testable by feeding synthetic API responses.

   TWO RULES THIS MODULE ENFORCES (and the test file locks in):

   1. The UI renders EXACTLY the referrer column of the field-visibility
      matrix (docs/contracts/crm-domain.v1.md §3) — the fields below are
      read off that column verbatim. Enforcement is SERVER-SIDE persona
      field redaction (locked decision 7); this list is presentation, and
      it may only narrow what the API returns, never widen it. No funding
      amounts, no KYC documents, no admin notes, no other-referrer data,
      no external refs, no ownership history — those keys are not declared
      here and the pages have no code path that renders them.

   2. NO SCAFFOLDING FOR HIDDEN FIELDS: `presentFields` keeps a descriptor
      only when its key is PRESENT on the response object. Hidden cells
      arrive as ABSENT keys (redaction omits, never nulls), so an absent
      key renders nothing at all — not an empty slot, not a placeholder —
      and the UI cannot leak the shape of what was hidden. (`null` means
      "visible but empty" and renders as an em dash.) */

/* Launch flag (crm_referrer_view, system_settings-backed, rides /auth/me).
   FAIL-CLOSED per FLAG-R-07: only an explicit boolean `true` enables —
   a missing user, missing feature_flags (flag endpoint unreadable),
   false, or any non-boolean value keeps the surface hidden. Deliberately
   NO localStorage/env override (unlike nav_v2): this is the 010-gated
   launch switch, not a QA cosmetic. */
export function isCrmReferrerViewEnabled(user) {
  return user?.feature_flags?.crm_referrer_view === true;
}

/* A list-endpoint 403 means the caller has tier-2 introducer standing:
   aggregates are their ONLY read surface (contract §3). The page renders
   the aggregates card alone on this signal. */
export function isAggregatesOnlyAccess(error) {
  return error?.response?.status === 403;
}

/* ── Formatters ─────────────────────────────────────────────────────── */

const CAPITAL_BAND_LABELS = {
  under_100k: "Under $100K",
  "100k_500k": "$100K – $500K",
  "500k_1m": "$500K – $1M",
  "1m_5m": "$1M – $5M",
  "5m_plus": "$5M+",
};

// A band is a RANGE label, never a dollar amount (locked decision 7).
export function formatCapitalBand(band) {
  if (!band) return null;
  return CAPITAL_BAND_LABELS[band] || humanize(band);
}

export function humanize(value) {
  if (!value) return null;
  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatCrmDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/* KYC/onboarding status FLAG (pass/pending/stuck — never documents). */
export const KYC_STATUS_BADGES = {
  pass: { label: "KYC Pass", cls: "bg-emerald/20 text-emerald" },
  pending: { label: "KYC Pending", cls: "bg-orange-500/20 text-orange-400" },
  stuck: { label: "KYC Stuck", cls: "bg-red-500/20 text-red-400" },
};

export function kycBadge(status) {
  if (!status) return null;
  return KYC_STATUS_BADGES[status] || { label: humanize(status), cls: "bg-slate-500/20 text-slate-400" };
}

/* ── Field descriptors (the referrer matrix column, verbatim) ───────── */

export const INVESTOR_LIST_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "company", label: "Company" },
  { key: "stage", label: "Stage", format: humanize },
  { key: "capitalBand", label: "Capital Band", format: formatCapitalBand },
  { key: "kycStatus", label: "KYC" },
  { key: "lastContactAt", label: "Last Contact", format: formatCrmDate },
];

export const INVESTOR_DETAIL_FIELDS = [
  { key: "company", label: "Company" },
  { key: "primaryEmail", label: "Email" },
  { key: "primaryPhone", label: "Phone" },
  { key: "stage", label: "Pipeline Stage", format: humanize },
  { key: "capitalBand", label: "Capital Band", format: formatCapitalBand },
  { key: "kycStatus", label: "KYC Status" },
  { key: "lastContactAt", label: "Last Contact", format: formatCrmDate },
];

/* Keep only descriptors whose key IS PRESENT on the response object —
   rule 2 above. Absent (redacted) keys yield no descriptor, so nothing
   is rendered for them: no empty slot, no label, no placeholder. */
export function presentFields(descriptors, obj) {
  if (!obj) return [];
  return descriptors.filter((d) => d.key in obj);
}

/* List columns: shown only when at least one row carries the key —
   the column set is derived from the data, never scaffolded. */
export function presentColumns(descriptors, rows) {
  if (!rows || rows.length === 0) return [];
  return descriptors.filter((d) => rows.some((row) => row && d.key in row));
}

/* Note attribution: redaction strips `authorExternalSubject` from notes
   the caller didn't author (other-referrer identity is hidden). Render
   "You" for own notes and nothing otherwise — never invent attribution. */
export function noteAuthorLabel(note, currentUserId) {
  if (note?.authorExternalSubject && note.authorExternalSubject === currentUserId) return "You";
  return null;
}
